/**
 * src/pages/Payment.tsx
 *
 * File-level:
 * - Payment page showing pricing plans, premium services and payment methods.
 * - Adds a dismissible header-level promotional hero (OfferHero) placed beside the page title.
 *
 * Notes:
 * - Dismissal state is persisted to localStorage with key `rjbd_offer_dismissed_v1`.
 * - Per-card ribbons were removed to prevent overlapping with card titles; the single header hero replaces them.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Star, DollarSign, Zap, Eye } from 'lucide-react';
import OfferHero from '../components/offers/OfferHero';
import { X } from 'lucide-react';
import { API_CONFIG } from '../config/api.config';

/**
 * LOCAL STORAGE KEY
 * @description Key used to persist offer dismissal state.
 */
const OFFER_DISMISSED_KEY = 'rjbd_offer_dismissed_v1';

/**
 * RAZORPAY CONFIGURATION
 * Replace with your actual Razorpay keys
 */
//const RAZORPAY_KEY_ID = 'rzp_test_YOUR_KEY_ID'; // Replace with your key
const RAZORPAY_KEY_ID = 'rzp_live_RpMnhChX5iH3MS'; // Live key

/**
 * Razorpay types
 */
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  image?: string;
  order_id?: string;
  handler: (response: RazorpayResponse) => void;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  theme?: {
    color: string;
  };
  modal?: {
    ondismiss?: () => void;
  };
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id?: string;
  razorpay_signature?: string;
}

/**
 * PaymentPage
 *
 * @description Renders pricing plans and payment sections with a header-level OfferHero.
 */
export default function PaymentPage(): JSX.Element {
  const navigate = useNavigate();
  const [offerDismissed, setOfferDismissed] = useState<boolean>(() => {
    try {
      return Boolean(localStorage.getItem(OFFER_DISMISSED_KEY));
    } catch {
      return false;
    }
  });
  const [isRazorpayLoaded, setIsRazorpayLoaded] = useState(false);

  /**
   * dismissOffer
   *
   * @description Mark the promotional offer as dismissed and persist to localStorage.
   */
  function dismissOffer() {
    try {
      localStorage.setItem(OFFER_DISMISSED_KEY, '1');
    } catch {
      // ignore storage errors (e.g. private mode)
    }
    setOfferDismissed(true);
  }

  /**
   * Load Razorpay script
   */
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setIsRazorpayLoaded(true);
    document.body.appendChild(script);
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    // Keep state in sync if storage changes from another tab
    function handleStorage(e: StorageEvent) {
      if (e.key === OFFER_DISMISSED_KEY) {
        setOfferDismissed(Boolean(e.newValue));
      }
    }
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  /**
   * Handle Razorpay payment
   */
  const handlePayment = (amount: number, planName: string, description: string) => {
    if (!isRazorpayLoaded) {
      alert('Payment gateway is loading. Please try again in a moment.');
      return;
    }

    const options: RazorpayOptions = {
      key: RAZORPAY_KEY_ID,
      amount: amount * 100, // Razorpay expects amount in paise
      currency: 'INR',
      name: 'RJBD Matrimonial',
      description: description,
      image: '/HomeImage/QRCode.png', // Your logo
      handler: async function (response: RazorpayResponse) {
        try {
          // Get logged-in user data
          const userDataStr = localStorage.getItem('userData');
          if (!userDataStr) {
            alert('User not logged in. Please login first.');
            return;
          }
          const userData = JSON.parse(userDataStr);
          const userId = userData.userId;

          // Calculate profile validity based on plan
          const now = new Date();
          let validityDate: Date;
          if (planName === 'Basic') {
            validityDate = new Date(now);
            validityDate.setMonth(validityDate.getMonth() + 3);
          } else if (planName === 'Premium') {
            validityDate = new Date(now);
            validityDate.setMonth(validityDate.getMonth() + 6);
          } else if (planName === 'Elite') {
            validityDate = new Date(now);
            validityDate.setFullYear(validityDate.getFullYear() + 1);
          } else {
            alert('Invalid plan selected');
            return;
          }

          // Update user payment status
          const updateResponse = await fetch(API_CONFIG.getApiEndpoint('users/update-user-payment'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: userId,
              paymentDone: 1,
              profileValidity: validityDate.toISOString(),
              paymentId: response.razorpay_payment_id,
              plan: planName
            }),
          });

          const updateResult = await updateResponse.json();
          if (updateResult.success) {
            // Insert payment record
            const paymentRecord = {
              userid: userId,
              planName: planName,
              duration: planName === 'Basic' ? '3 months' : planName === 'Premium' ? '6 months' : '1 year',
              paymentMethod: 'Razorpay', // Razorpay doesn't provide method in handler, assuming online
              transactionId: response.razorpay_payment_id,
              amount: amount,
              status: 'Successful',
              startDate: now.toISOString(),
              endDate: validityDate.toISOString()
            };

            await fetch(API_CONFIG.getApiEndpoint('payments/insert-payment-record'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(paymentRecord),
            });

            alert(`Payment Successful!\nPayment ID: ${response.razorpay_payment_id}\nProfile upgraded successfully!`);
            // Navigate to Dashboard
            navigate('/dashboard');
          } else {
            alert('Payment successful but failed to update profile. Please contact support.');
          }
        } catch (error) {
          console.error('Error updating payment:', error);
          alert('Payment successful but error occurred. Please contact support.');
        }
      },
      prefill: {
        name: '',
        email: '',
        contact: ''
      },
      theme: {
        color: '#E91E63' // Rose color matching your theme
      },
      modal: {
        ondismiss: function() {
          console.log('Payment cancelled by user');
        }
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const plans = [
    {
      name: 'Basic',
      price: '₹2000',
      discountedPrice: '₹1000',
      duration: '3 months',
      popular: false,
      features: [
        'Profile Listing',
        'Basic Search',
        'Send 300 Interests',
        'View 300 Contacts',
        'Dedicated Support'
      ]
    },
    {
      name: 'Premium',
      price: '₹6,000',
      discountedPrice: '₹3,000',
      duration: '6 months',
      popular: true,
      features: [
        'Priority Listing',
        'Advanced Search',
        'Unlimited Interests',
        'View All Contacts',
        'Chat Feature',
        'Profile Highlight',
        'Dedicated Support'
      ]
    },
    {
      name: 'Elite',
      price: '₹10,000',
      discountedPrice: '₹5,000',
      duration: '12 months',
      popular: false,
      features: [
        'Top Priority Listing',
        'All Premium Features',
        'Personal Matchmaking',
        'Video Profile',
        'Background Verification',
        'Dedicated Support'
      ]
    }
  ];

  const paymentMethods = [
    { name: 'Credit Card', icon: '💳' },
    { name: 'Debit Card', icon: '💳' },
    { name: 'UPI', icon: '📱' },
    { name: 'Net Banking', icon: '🏦' },
    { name: 'Wallet', icon: '👛' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0b3b82] via-[#5f1c7c] to-[#ff2d87] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header row: title left, OfferHero right (stacks on small screens) */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-6 gap-4">
          <div className="text-left">
            <h1 className="text-4xl font-bold text-rose-800 mb-1">Choose Your Plan</h1>
            <p className="text-rose-600 text-lg">Upgrade your experience and find your perfect match faster</p>
          </div>

          {/* Header-level hero (bigger visual) */}
          {!offerDismissed && (
            <OfferHero text="Independence Day Dhamaka — Offer! 50% Discount on all plans" onDismiss={dismissOffer} />
          )}
        </div>

        {/* Pricing Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan, index) => (
            <Card key={index} className={`relative ${plan.popular ? 'border-rose-300 shadow-xl scale-105' : ''}`}>
              {/* NOTE: per-plan ribbon removed to avoid overlapping card title */}

              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-rose-600 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" />
                    Most Popular
                  </div>
                </div>
              )}
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <div className="flex flex-col items-center gap-1">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-xl line-through text-gray-400">{plan.price}</span>
                    <span className="text-3xl font-bold text-rose-700">{plan.discountedPrice}</span>
                  </div>
                  <span className="text-gray-600">/{plan.duration}</span>
                  <span className="text-xs text-green-600 font-semibold mt-1">Ramadan Offer - 50% discount</span>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-3">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => {
                    const amount = parseInt(plan.discountedPrice.replace('₹', '').replace(',', ''));
                    handlePayment(amount, plan.name, `${plan.name} Plan - ${plan.duration}`);
                  }}
                  className={`w-full ${
                    plan.popular
                      ? 'bg-rose-600 hover:bg-rose-700'
                      : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                >
                  Choose Plan
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payment Methods */}
        <Card>
          <CardContent>
            {/* UPI Payment Section */}
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4">UPI Payment</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">UPI ID: rjbdmat@ptyes </label>
                    <input 
                      type="text" 
                      value="rjbdmat@ptyes" 
                      readOnly
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent bg-gray-50"
                    />
                  </div>
                  <Button className="w-full bg-rose-600 hover:bg-rose-700">Pay via UPI</Button>
                </div>
                <div className="text-center">
                  <div className="bg-white p-6 rounded-lg border-2 border-dashed border-gray-300">
                    <p className="text-sm text-gray-600 mb-4">Scan QR Code</p>
                    <div className="w-48 h-48 md:w-56 md:h-56 mx-auto flex items-center justify-center rounded overflow-hidden">
                      <img 
                        src="/HomeImage/QRCode.png" 
                        alt="UPI QR Code" 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-4">Scan with any UPI app</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Note */}
            <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 text-center">
                🔒 Your payment is secure and encrypted. We never store your payment details.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}